import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Briefcase, Check, ShieldCheck } from "lucide-react";
import SEO from "../components/common/SEO";
import PageHero from "../components/common/PageHero";
import { Reveal } from "../components/common/Reveal";
import ResumeForm from "../components/forms/ResumeForm";
import SaveJobButton from "../components/careers/SaveJobButton";
import ShareJob from "../components/careers/ShareJob";
import { useContent } from "../context/ContentContext";
import { getToken, getSaved, addSaved, removeSaved } from "../lib/api";

export default function JobDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { getJob, loading } = useContent();
  const job = getJob(slug);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!slug || !getToken()) return;
    getSaved().then((d) => setSaved((d.saved || []).includes(slug))).catch(() => {});
  }, [slug]);

  const toggleSave = async () => {
    if (!getToken()) {
      toast.info("Sign in to save jobs", { description: "Create a portal session to bookmark roles." });
      navigate("/portal/login");
      return;
    }
    try {
      if (saved) {
        await removeSaved(slug);
        setSaved(false);
        toast.success("Removed from saved jobs");
      } else {
        await addSaved(slug);
        setSaved(true);
        toast.success("Job saved");
      }
    } catch {
      toast.error("Could not update saved jobs");
    }
  };

  if (!job) {
    // Content still loading: avoid a false "not found" flash while jobs stream in.
    if (loading) {
      return (
        <>
          <SEO title="Loading role…" description="Loading position details." path={`/careers/${slug}`} />
          <div className="flex min-h-[60vh] items-center justify-center px-6" data-testid="job-loading">
            <div className="flex flex-col items-center gap-4 text-slate-400">
              <div className="h-10 w-10 animate-spin rounded-full border-2 border-white/10 border-t-sky-400" />
              <p className="text-sm">Loading position…</p>
            </div>
          </div>
        </>
      );
    }
    // Content loaded and the role genuinely doesn't exist: show a friendly fallback (not a hard 404).
    return (
      <>
        <SEO title="Position not found" description="This role is no longer available." path={`/careers/${slug}`} />
        <section className="flex min-h-[70vh] items-center justify-center px-6 py-24" data-testid="job-not-found">
          <div className="mx-auto max-w-lg text-center">
            <div className="mx-auto mb-6 inline-flex h-16 w-16 items-center justify-center rounded-2xl border border-white/10 bg-ink-800/60 text-sky-400">
              <Briefcase className="h-7 w-7" />
            </div>
            <h1 className="font-heading text-3xl font-bold tracking-tight text-white sm:text-4xl">This role isn&apos;t available</h1>
            <p className="mt-4 text-slate-400">
              The position you&apos;re looking for may have been filled or is no longer open. Explore our other current opportunities.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/careers"
                className="inline-flex items-center gap-2 rounded-full bg-sky-400 px-6 py-3 text-sm font-medium text-ink-900 transition-colors hover:bg-sky-300"
                data-testid="job-not-found-careers"
              >
                <ArrowLeft className="h-4 w-4" /> View all open positions
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-3 text-sm font-medium text-white transition-colors hover:border-sky-400/60 hover:text-sky-400"
                data-testid="job-not-found-contact"
              >
                Contact our team
              </Link>
            </div>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <SEO title={`${job.role} — Careers`} description={job.summary} path={`/careers/${slug}`} />
      <PageHero eyebrow="Open Position" title={job.role} subtitle={job.summary}>
        <div className="flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-sm text-sky-400">
            <Briefcase className="h-3.5 w-3.5" /> {job.type}
          </span>
          <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-sm text-slate-300">
            <MapPin className="h-3.5 w-3.5" /> {job.location}
          </span>
          <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-sm text-slate-300">
            {job.skill}
          </span>
          {job.work_authorization ? (
            <span
              className="inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3 py-1.5 text-sm font-medium text-emerald-300"
              data-testid="job-work-auth-chip"
            >
              <ShieldCheck className="h-3.5 w-3.5" /> {job.work_authorization}
            </span>
          ) : null}
          <SaveJobButton saved={saved} onClick={toggleSave} withLabel testId="job-save-btn" />
        </div>
        <div className="mt-6">
          <ShareJob job={job} />
        </div>
      </PageHero>

      <section className="px-6 pb-24">
        <div className="mx-auto max-w-7xl">
          <Link to="/careers" className="inline-flex items-center gap-2 text-sm text-slate-400 transition-colors hover:text-sky-400" data-testid="job-back">
            <ArrowLeft className="h-4 w-4" /> All Positions
          </Link>

          <div className="mt-8 grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <Reveal>
                <h2 className="font-heading text-2xl font-bold tracking-tight text-white">What you&apos;ll do</h2>
                <ul className="mt-5 grid gap-3">
                  {job.responsibilities.map((r) => (
                    <li key={r} className="flex items-start gap-3 text-sm text-slate-300">
                      <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-sky-400/15 text-sky-400">
                        <Check className="h-3 w-3" />
                      </span>
                      {r}
                    </li>
                  ))}
                </ul>
              </Reveal>
              <Reveal delay={0.1}>
                <h2 className="mt-12 font-heading text-2xl font-bold tracking-tight text-white">What we&apos;re looking for</h2>
                <ul className="mt-5 grid gap-3">
                  {job.requirements.map((r) => (
                    <li key={r} className="flex items-start gap-3 text-sm text-slate-300">
                      <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-sky-400/15 text-sky-400">
                        <Check className="h-3 w-3" />
                      </span>
                      {r}
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>

            <div className="lg:col-span-5">
              <Reveal delay={0.15}>
                <div id="apply" className="sticky top-28 rounded-2xl border border-white/10 bg-ink-800/50 p-8">
                  <h2 className="font-heading text-xl font-bold tracking-tight text-white">Apply Now</h2>
                  <p className="mt-2 text-sm text-slate-400">Submit your application for {job.role}.</p>
                  {job.work_authorization ? (
                    <div className="mt-5 flex items-start gap-3 rounded-xl border border-emerald-400/25 bg-emerald-400/10 px-4 py-3" data-testid="job-work-auth-callout">
                      <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-300" />
                      <div>
                        <div className="text-xs font-medium uppercase tracking-[0.15em] text-emerald-300/80">Work Authorization</div>
                        <div className="mt-0.5 text-sm font-medium text-white">{job.work_authorization}</div>
                      </div>
                    </div>
                  ) : null}
                  <div className="mt-6">
                    <ResumeForm initialRole={job.role} />
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
